/**
 * Data hooks for the Agent-page channel tabs
 * (`ai-docs/lexide-agent-channel-tabs-impl-plan.md` §3.3). Mirror the tasklist
 * hooks: fetch + poll + refetch-on-focus, plus a `channels.changed` subscription
 * that is a no-op today (no such broadcast exists yet) but keeps the mandatory
 * `plugin.` namespace rule in the ONE helper for whenever the runtime adds it.
 *
 * - `useChannelsInventory()` — the `channels.list` inventory + a gate.
 * - `useChannelConversations(channel, {enabled})` — one channel's peer list.
 * - `useChannelThread(channel, peerKey, {enabled})` — one thread, paged backwards.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { useGatewayStore } from "@/stores/useGatewayStore";
import { useLexEventStream } from "@/hooks/useEventStream";
import {
  listChannels,
  listConversations,
  listMessages,
  type ChannelConversation,
  type ChannelMessage,
  type ChannelSummary,
} from "@/api/lx-channels";
import { filterNullPeerRows, mergeOlderPage } from "@/lib/channel-scope";

/** Visible-tab poll cadence — same rationale as the tasklist: the primary push
 *  signal (`channels.changed`) does not exist yet, so the poll IS the freshness
 *  guarantee while the lawyer watches. */
const POLL_MS = 20_000;
/** Hidden tab: stop burning requests nobody is looking at. */
const POLL_HIDDEN_MS = 120_000;
/** Thread page size — well under the server's 500 clamp. */
const PAGE = 100;
/**
 * Null-peer bucket: how many raw `channels.messages` pages one gesture (initial
 * load or a single "load older" click) may auto-walk looking for stash-miss
 * rows. `channels.messages` can't filter to `peer:null` (impl plan §7), so we
 * fetch-and-filter — but an unbounded walk on a busy channel with few null rows
 * would swallow the whole history in one gesture. Cap it; the manual control
 * reappears so the lawyer stays in control of the cost.
 */
const NULL_BUCKET_MAX_AUTO_PAGES = 5;

/** Matches the runtime's "no such method" rejection (mirrors `useTasklistGate`). */
function isMethodNotFound(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /method|unknown|not.?found|unsupported|disabled/i.test(msg);
}

/** Is the gateway RPC usable right now? Guards the "gateway not connected" throw
 *  so a cold-load probe isn't mis-classified as a real error (or a gate signal). */
function gatewayReady(): boolean {
  return useGatewayStore.getState().connectionState === "connected";
}

/**
 * Shared refresh wiring: focus + visibility refetch, optional interval poll, and
 * the future `channels.changed` subscription. Kept in one place so the three
 * hooks below can't drift on the namespace rule or the visibility cadence.
 */
function useChannelAutoRefresh(fetchFn: () => void, poll: boolean): void {
  const fnRef = useRef(fetchFn);
  fnRef.current = fetchFn;

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined;
    const tick = () => fnRef.current();
    const schedule = () => {
      if (!poll) return;
      if (timer) clearInterval(timer);
      const hidden =
        typeof document !== "undefined" && document.visibilityState === "hidden";
      timer = setInterval(tick, hidden ? POLL_HIDDEN_MS : POLL_MS);
    };
    schedule();

    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
      schedule();
    };
    const onFocus = () => tick();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, [poll]);

  // Bare `channels.changed` — `useLexEventStream` resolves the `plugin.` wire
  // namespace core requires for delivery. No-op until the runtime emits it.
  useLexEventStream(
    "channels.changed",
    useCallback(() => fnRef.current(), []),
  );
}

export type ChannelsGate = "loading" | "enabled" | "disabled";

export interface UseChannelsInventoryResult {
  channels: ChannelSummary[];
  gate: ChannelsGate;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * `channels.list` + a gate. `gate:"loading"` until the gateway connects and the
 * first probe returns; `"disabled"` ONLY on a method-not-found rejection (a
 * runtime whose image predates `channels.*` — there is NO env gate, the methods
 * register unconditionally); `"enabled"` once the surface answers (a non
 * method-not-found error keeps it enabled and surfaces as a pane error — a blip
 * must not swap the tab back to its placeholder).
 */
export function useChannelsInventory(): UseChannelsInventoryResult {
  const connectionState = useGatewayStore((s) => s.connectionState);
  const [channels, setChannels] = useState<ChannelSummary[]>([]);
  const [gate, setGate] = useState<ChannelsGate>("loading");
  const [error, setError] = useState<string | null>(null);
  const disposedRef = useRef(false);

  useEffect(() => {
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
    };
  }, []);

  const fetchInventory = useCallback(async () => {
    if (!gatewayReady()) return; // stay in the current gate (loading) until connected
    try {
      const res = await listChannels();
      if (disposedRef.current) return;
      setChannels(res.channels ?? []);
      setGate("enabled");
      setError(null);
    } catch (err) {
      if (disposedRef.current) return;
      if (!gatewayReady()) return; // a mid-flight disconnect is not a gate signal
      if (isMethodNotFound(err)) {
        setGate("disabled");
        setError(null);
      } else {
        // The method exists (it errored, it wasn't "unknown") → keep the tab up
        // and show the error, rather than hiding behind the placeholder.
        setGate("enabled");
        setError(err instanceof Error ? err.message : "channels unavailable");
      }
    }
  }, []);

  // Initial + reconnect fetch.
  useEffect(() => {
    if (connectionState === "connected") void fetchInventory();
  }, [connectionState, fetchInventory]);

  useChannelAutoRefresh(() => void fetchInventory(), true);

  return { channels, gate, error, refetch: fetchInventory };
}

export interface UseChannelConversationsResult {
  conversations: ChannelConversation[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/** `channels.conversations` for one channel — newest first, `peer:null` bucket
 *  included. Gated by `enabled` (the pane only fetches the active tab). */
export function useChannelConversations(
  channel: string,
  opts: { enabled: boolean },
): UseChannelConversationsResult {
  const { enabled } = opts;
  const connectionState = useGatewayStore((s) => s.connectionState);
  const [conversations, setConversations] = useState<ChannelConversation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const disposedRef = useRef(false);

  useEffect(() => {
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
    };
  }, []);

  const fetchConversations = useCallback(async () => {
    if (!enabled || !gatewayReady()) return;
    setIsLoading(true);
    try {
      const res = await listConversations(channel);
      if (disposedRef.current) return;
      setConversations(res.conversations ?? []);
      setError(null);
    } catch (err) {
      if (disposedRef.current) return;
      setError(err instanceof Error ? err.message : "channel unavailable");
    } finally {
      if (!disposedRef.current) setIsLoading(false);
    }
  }, [channel, enabled]);

  useEffect(() => {
    if (enabled && connectionState === "connected") void fetchConversations();
  }, [enabled, connectionState, fetchConversations]);

  useChannelAutoRefresh(() => void fetchConversations(), enabled);

  return { conversations, isLoading, error, refetch: fetchConversations };
}

export interface UseChannelThreadResult {
  messages: ChannelMessage[];
  /** Full thread size (from the head fetch); informational, NOT the paging driver. */
  total: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  loadOlder: () => Promise<void>;
  refetch: () => Promise<void>;
}

interface Page {
  rows: ChannelMessage[];
  nextBefore?: number;
  /** Present only on a string-peer head fetch (the wire `total`). */
  total?: number;
}

/**
 * `channels.messages` for one thread. `peerKey` is a string peer (filtered
 * server-side) or `null` for the stash-miss bucket (filtered client-side, with
 * a bounded auto-walk — see `NULL_BUCKET_MAX_AUTO_PAGES`). Pages backwards on
 * the stored `nextBefore` cursor; `hasMore` is defined SOLELY as "the last
 * response carried a `nextBefore`", never derived from `total`.
 *
 * No interval poll (unlike the list): a poll would reset a lawyer's loaded
 * older pages mid-read. It refetches the head on focus + the future
 * `channels.changed` event, which is the right freshness cadence for a thread.
 */
export function useChannelThread(
  channel: string,
  peerKey: string | null,
  opts: { enabled: boolean },
): UseChannelThreadResult {
  const { enabled } = opts;
  const connectionState = useGatewayStore((s) => s.connectionState);
  const [messages, setMessages] = useState<ChannelMessage[]>([]);
  const [total, setTotal] = useState(0);
  const [cursor, setCursor] = useState<number | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const disposedRef = useRef(false);
  const cursorRef = useRef<number | undefined>(undefined);
  cursorRef.current = cursor;

  useEffect(() => {
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
    };
  }, []);

  // One logical page. String peer → one raw fetch (server filters). Null bucket
  // → walk older raw pages (bounded) until some stash-miss rows surface or the
  // head is reached; the returned `nextBefore` is the raw server cursor so the
  // next gesture continues the scan exactly where this one stopped.
  const fetchPage = useCallback(
    async (before?: number): Promise<Page> => {
      if (peerKey !== null) {
        const res = await listMessages({
          channel,
          peer: peerKey,
          ...(before !== undefined ? { before } : {}),
          limit: PAGE,
        });
        return {
          rows: res.messages ?? [],
          nextBefore: res.nextBefore,
          ...(before === undefined ? { total: res.total } : {}),
        };
      }
      let scan = before;
      for (let i = 0; i < NULL_BUCKET_MAX_AUTO_PAGES; i++) {
        const res = await listMessages({
          channel,
          ...(scan !== undefined ? { before: scan } : {}),
          limit: PAGE,
        });
        const nulls = filterNullPeerRows(res.messages ?? []);
        if (nulls.length > 0 || res.nextBefore === undefined) {
          return { rows: nulls, nextBefore: res.nextBefore };
        }
        scan = res.nextBefore;
      }
      // Hit the auto cap without finding rows — expose the cursor so a manual
      // "load older" can resume the walk from here.
      return { rows: [], nextBefore: scan };
    },
    [channel, peerKey],
  );

  const fetchHead = useCallback(async () => {
    if (!enabled || !gatewayReady()) return;
    setIsLoading(true);
    try {
      const page = await fetchPage(undefined);
      if (disposedRef.current) return;
      setMessages(page.rows);
      setCursor(page.nextBefore);
      setHasMore(page.nextBefore !== undefined);
      setTotal(page.total ?? page.rows.length);
      setError(null);
    } catch (err) {
      if (disposedRef.current) return;
      setError(err instanceof Error ? err.message : "thread unavailable");
    } finally {
      if (!disposedRef.current) setIsLoading(false);
    }
  }, [enabled, fetchPage]);

  const loadOlder = useCallback(async () => {
    const before = cursorRef.current;
    if (before === undefined || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const page = await fetchPage(before);
      if (disposedRef.current) return;
      setMessages((prev) => mergeOlderPage(prev, page.rows));
      setCursor(page.nextBefore);
      setHasMore(page.nextBefore !== undefined);
    } catch (err) {
      if (disposedRef.current) return;
      setError(err instanceof Error ? err.message : "thread unavailable");
    } finally {
      if (!disposedRef.current) setIsLoadingMore(false);
    }
  }, [fetchPage, isLoadingMore]);

  useEffect(() => {
    if (enabled && connectionState === "connected") void fetchHead();
  }, [enabled, connectionState, fetchHead]);

  // Focus + event only (no interval) so paged-back state survives.
  useChannelAutoRefresh(() => void fetchHead(), false);

  return {
    messages,
    total,
    hasMore,
    isLoading,
    isLoadingMore,
    error,
    loadOlder,
    refetch: fetchHead,
  };
}
